import React from 'react'
import {
  AlertCircle, ChevronDown, ChevronUp, Hash, Image as ImageIcon, KeyRound, Link2, List,
  Minus, Plus, Trash2, Type
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MailBlock, MailBlockType, MailTone, TemplateVariable } from '../../types/communications'

interface Props {
  blocks: MailBlock[]
  variables: TemplateVariable[]
  onChange: (blocks: MailBlock[]) => void
}

const BLOCK_ICONS: Record<MailBlockType, React.ElementType> = {
  paragraph: Type,
  bullets: List,
  callout: AlertCircle,
  code: KeyRound,
  facts: Hash,
  button: Link2,
  image: ImageIcon,
  divider: Minus
}

const TONES: MailTone[] = ['info', 'success', 'warning', 'critical']

const emptyBlock = (type: MailBlockType): MailBlock => {
  if (type === 'bullets') return { type, items: [''] }
  if (type === 'facts') return { type, rows: [{ label: '', value: '' }] }
  if (type === 'code') return { type, label: '', value: '' }
  if (type === 'button') return { type, label: '', url: '' }
  if (type === 'callout') return { type, text: '', tone: 'info' }
  if (type === 'image') return { type, url: '', alt: '', caption: '', href: '' }
  if (type === 'divider') return { type }
  return { type: 'paragraph', text: '' }
}

const MailBlockEditor: React.FC<Props> = ({ blocks, variables, onChange }) => {
  const { t } = useTranslation()
  // Clicking a variable adds it to whichever text box was last focused.
  const [focused, setFocused] = React.useState<{ index: number; field: string } | null>(null)

  const replace = (index: number, block: MailBlock) =>
    onChange(blocks.map((item, position) => (position === index ? block : item)))

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= blocks.length) return
    const next = [...blocks]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const insertVariable = (variable: string) => {
    if (!focused) return
    const block = blocks[focused.index]
    if (!block) return
    const token = `{{${variable}}}`
    const append = (value?: string) => `${value || ''}${value && !value.endsWith(' ') ? ' ' : ''}${token}`
    const [field, position] = focused.field.split(':')
    if (field === 'items' && block.items) {
      const items = [...block.items]
      items[Number(position)] = append(items[Number(position)])
      replace(focused.index, { ...block, items })
    } else if (field === 'rowLabel' || field === 'rowValue') {
      const rows = [...(block.rows || [])]
      const key = field === 'rowLabel' ? 'label' : 'value'
      rows[Number(position)] = { ...rows[Number(position)], [key]: append(rows[Number(position)]?.[key]) }
      replace(focused.index, { ...block, rows })
    } else {
      replace(focused.index, { ...block, [field]: append(block[field as 'text'] as string) })
    }
  }

  const textArea = (index: number, field: 'text', value: string, rows = 3) =>
    <textarea rows={rows} value={value} onFocus={() => setFocused({ index, field })}
      onChange={event => replace(index, { ...blocks[index], [field]: event.target.value })}
      className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />

  const line = (index: number, field: string, value: string, placeholder: string, type = 'text') =>
    <input type={type} value={value} placeholder={placeholder} onFocus={() => setFocused({ index, field })}
      onChange={event => replace(index, { ...blocks[index], [field]: event.target.value })}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />

  return <div className={`grid grid-cols-1 gap-3 ${variables.length ? 'lg:grid-cols-[minmax(0,1fr)_220px]' : ''}`}>
    <div className="space-y-2">
      {blocks.length === 0 && <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500">
        {t('communications.blocks.empty')}
      </p>}

      {blocks.map((block, index) => {
        const Icon = BLOCK_ICONS[block.type] || Type
        return <article key={index} className="rounded-xl border border-gray-200 bg-white">
          <header className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <Icon size={13} className="text-blue-600" />
              {t(`communications.blocks.types.${block.type}`)}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(index, -1)} disabled={index === 0} title={t('communications.blocks.moveUp')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30">
                <ChevronUp size={14} />
              </button>
              <button onClick={() => move(index, 1)} disabled={index === blocks.length - 1} title={t('communications.blocks.moveDown')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30">
                <ChevronDown size={14} />
              </button>
              <button onClick={() => onChange(blocks.filter((_, position) => position !== index))}
                title={t('communications.blocks.remove')}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                <Trash2 size={14} />
              </button>
            </div>
          </header>

          <div className="space-y-2 p-3">
            {block.type === 'paragraph' && textArea(index, 'text', block.text || '')}

            {block.type === 'callout' && <>
              {textArea(index, 'text', block.text || '', 2)}
              <div className="flex flex-wrap gap-1">
                {TONES.map(tone => <button key={tone} onClick={() => replace(index, { ...block, tone })}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
                    (block.tone || 'info') === tone ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>{t(`communications.blocks.tones.${tone}`)}</button>)}
              </div>
            </>}

            {block.type === 'bullets' && <>
              {(block.items || []).map((item, position) => <div key={position} className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <input value={item} onFocus={() => setFocused({ index, field: `items:${position}` })}
                  onChange={event => replace(index, {
                    ...block,
                    items: (block.items || []).map((value, at) => (at === position ? event.target.value : value))
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500" />
                <button onClick={() => replace(index, { ...block, items: (block.items || []).filter((_, at) => at !== position) })}
                  className="rounded p-1 text-gray-400 hover:text-red-600"><Trash2 size={13} /></button>
              </div>)}
              <button onClick={() => replace(index, { ...block, items: [...(block.items || []), ''] })}
                className="text-[11px] font-semibold text-blue-600">+ {t('communications.blocks.addItem')}</button>
            </>}

            {block.type === 'facts' && <>
              {(block.rows || []).map((row, position) => <div key={position} className="flex items-center gap-2">
                <input value={row.label} placeholder={t('communications.blocks.factLabel')}
                  onFocus={() => setFocused({ index, field: `rowLabel:${position}` })}
                  onChange={event => replace(index, {
                    ...block,
                    rows: (block.rows || []).map((item, at) => (at === position ? { ...item, label: event.target.value } : item))
                  })}
                  className="w-1/3 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500" />
                <input value={row.value} placeholder={t('communications.blocks.factValue')}
                  onFocus={() => setFocused({ index, field: `rowValue:${position}` })}
                  onChange={event => replace(index, {
                    ...block,
                    rows: (block.rows || []).map((item, at) => (at === position ? { ...item, value: event.target.value } : item))
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500" />
                <button onClick={() => replace(index, { ...block, rows: (block.rows || []).filter((_, at) => at !== position) })}
                  className="rounded p-1 text-gray-400 hover:text-red-600"><Trash2 size={13} /></button>
              </div>)}
              <button onClick={() => replace(index, { ...block, rows: [...(block.rows || []), { label: '', value: '' }] })}
                className="text-[11px] font-semibold text-blue-600">+ {t('communications.blocks.addRow')}</button>
            </>}

            {block.type === 'code' && <>
              {line(index, 'label', block.label || '', t('communications.blocks.codeLabel'))}
              {line(index, 'value', block.value || '', t('communications.blocks.codeValue'))}
            </>}

            {block.type === 'button' && <>
              {line(index, 'label', block.label || '', t('communications.blocks.buttonLabel'))}
              {line(index, 'url', block.url || '', 'https://', 'url')}
            </>}

            {block.type === 'image' && <>
              {line(index, 'url', block.url || '', t('communications.blocks.imageUrl'), 'url')}
              {line(index, 'alt', block.alt || '', t('communications.blocks.imageAlt'))}
              {line(index, 'caption', block.caption || '', t('communications.blocks.imageCaption'))}
              {line(index, 'href', block.href || '', t('communications.blocks.imageLink'), 'url')}
              {/^https?:\/\//i.test(block.url || '')
                ? <img src={block.url} alt={block.alt || ''} className="max-h-40 rounded-lg border border-gray-200 object-contain" />
                : <p className="text-[11px] text-gray-500">{t('communications.blocks.imageHint')}</p>}
            </>}

            {block.type === 'divider' && <p className="text-[11px] text-gray-500">{t('communications.blocks.dividerHint')}</p>}
          </div>
        </article>
      })}

      <div className="flex flex-wrap gap-2 rounded-xl border border-dashed border-gray-300 p-3">
        {(Object.keys(BLOCK_ICONS) as MailBlockType[]).map(type => {
          const Icon = BLOCK_ICONS[type]
          return <button key={type} onClick={() => onChange([...blocks, emptyBlock(type)])}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50">
            <Plus size={11} /><Icon size={12} className="text-blue-600" />
            {t(`communications.blocks.types.${type}`)}
          </button>
        })}
      </div>
    </div>

    {variables.length > 0 && <aside className="rounded-xl border border-gray-200 bg-white p-3">
      <h4 className="text-xs font-semibold text-gray-800">{t('communications.blocks.insertData')}</h4>
      <p className="mt-1 text-[10px] leading-4 text-gray-500">{t('communications.blocks.insertHint')}</p>
      <div className="mt-3 max-h-[420px] space-y-1.5 overflow-y-auto pr-1">
        {variables.map(variable => <button key={variable.key} onClick={() => insertVariable(variable.key)}
          disabled={!focused}
          className="block w-full rounded-lg border border-gray-200 p-2 text-left hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50">
          <span className="block text-[11px] font-semibold text-gray-800">{variable.label}</span>
          {variable.sample && <span className="mt-0.5 block truncate text-[10px] text-gray-500">{variable.sample}</span>}
        </button>)}
      </div>
    </aside>}
  </div>
}

export default MailBlockEditor
